import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, FC, MouseEvent, PropsWithChildren, ReactNode, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { v4 as generateUuid } from 'uuid'

import iconCrossSvg from '@/img/icon-cross.svg'
import iconBackSvg from '@/img/icon-back.svg'
import {
  SC_DrawerWrapper,
  SC_DrawerContent,
  SC_DrawerBackdrop,
  SC_HeadingWithCloseBtnWrapper,
  SC_BackWithCloseBtnWrapper,
  SC_HeaderCard,
  SC_Body,
} from './styled'


export interface T_DrawerProps {
  heading?: string
  backTitle?: string
  onClose?: () => void
  onBackClick?: () => void
  isFullScreen?: boolean
  isBackdropClosable?: boolean
  isWithoutBackdrop?: boolean
  isWithoutDragLine?: boolean
  withHeaderCard?: boolean;
  headerRight?: ReactNode;
  scrollRootRef?: RefObject<HTMLDivElement>
  contentStyle?: CSSProperties
}

export const Drawer: FC<PropsWithChildren<T_DrawerProps>> = (props) => {
  const [ isNodeCreated, setIsNodeCreated ] = useState<boolean>(false)

  const drawerPortalRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const drawer = document.createElement('div')

    const uuid = generateUuid()
    const fullId = `drawer-portal-${uuid}`
    drawer.id = fullId

    document.body.appendChild(drawer)

    drawerPortalRef.current = document.getElementById(fullId)

    setIsNodeCreated(true)

    return () => {
      const foundNode = document.getElementById(fullId)
      if (foundNode) foundNode.remove()
    }
  }, [])

  const {
    heading,
    backTitle,
    onClose = () => {},
    onBackClick,
    isFullScreen,
    isBackdropClosable,
    isWithoutBackdrop = false,
    children,
    withHeaderCard,
    headerRight = <span></span>,
    scrollRootRef,
    contentStyle,
    ...restProps
  } = props

  const closeByBackdrop = (e: MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).dataset.drawerBackdrop) {
      onClose()
    }
  }

  const handleBackClick = () => onBackClick ? onBackClick() : onClose()

  if (!isNodeCreated || !drawerPortalRef.current) {
    return null
  }


  return createPortal(
    <SC_DrawerWrapper>
      {!isWithoutBackdrop && (
        <SC_DrawerBackdrop
          data-drawer-backdrop
          onClick={isBackdropClosable ? closeByBackdrop : () => {}}
        />
      )}

      <SC_DrawerContent
        ref={scrollRootRef}
        $isFullScreen={isFullScreen}
        $withHeaderCard={withHeaderCard}
        style={contentStyle}
        {...restProps}
      >
        {!!heading && (
          <SC_HeadingWithCloseBtnWrapper>
            <span>{heading}</span>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingRight: '6px',
            }}>
              {headerRight}
              <img src={iconCrossSvg} onClick={onClose} />
            </div>
          </SC_HeadingWithCloseBtnWrapper>
        )}

        {!!backTitle && (
          withHeaderCard ? (
            <SC_HeaderCard>
              <SC_BackWithCloseBtnWrapper
                $withHeaderCard={withHeaderCard}
                onClick={handleBackClick}
              >
                <img src={iconBackSvg} />

                <span>{backTitle}</span>

                {headerRight && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {headerRight}
                  </div>
                )}
              </SC_BackWithCloseBtnWrapper>
            </SC_HeaderCard>
          ) : (
            <SC_BackWithCloseBtnWrapper onClick={handleBackClick}>
              <img src={iconBackSvg} />

              <span>{backTitle}</span>

              {headerRight && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {headerRight}
                </div>
              )}
            </SC_BackWithCloseBtnWrapper>
          )
        )}

        <SC_Body $withHeaderCard={withHeaderCard}>
          <div>{children}</div>
        </SC_Body>
      </SC_DrawerContent>
    </SC_DrawerWrapper>,
    drawerPortalRef.current,
  )
}
